import { reactive } from 'vue';
import axios from 'axios';
import { buildVizProps } from '@/utils/metricInsightAdapter';

const SESSIONS = new Map();

const ARTIFACT_LABELS = {
  metric: 'Metric', update_metric: 'Metric', rebuild_metric: 'Metric',
  data_attribute: 'DataAttribute', update_data_attribute: 'DataAttribute',
  flag: 'Patient flag', worklist: 'Worklist', insight: 'Insight',
  study: 'R study', update_study: 'R study', run_study: 'R run', dataset: 'Dataset',
  data_parser_schema: 'Parser schema', update_data_parser_schema: 'Parser schema',
  enable_schema: 'Parser schema', parse_file: 'Parser schema',
  routine: 'Routine', update_routine: 'Routine', run_parse: 'Routine',
  data_workflow: 'Workflow', update_data_workflow: 'Workflow',
};

const NOTEBOOK_ARTIFACT_KINDS = new Set(['visual', 'drawing', 'page', 'dashboard', 'plan', 'file', 'document', 'proposal', 'inputs', 'run']);
const ENGINES = new Set(['snowflake', 'postgres', 'r']);
const DRAFT_LABELS = { data_attribute: 'DataAttribute', metric: 'Metric', dataset: 'Dataset', study: 'R study', schema: 'Parser schema' };
const DRAFT_LEDGER_KIND = { data_attribute: 'data_attribute', metric: 'metric', dataset: 'dataset', study: 'study', schema: 'schema' };

// Pulls ```html documents (and any preceding ```kairon-sources fence) out of an
// agent turn. Mirrors AgentConversation.segments, which still splits the same
// text for display — this one only cares about the documents.
function contentFingerprint(content) {
  const text = typeof content === 'string' ? content.replace(/\s+/g, ' ').trim() : JSON.stringify(content || {});
  let h = 0;
  for (let i = 0; i < text.length; i += 1) h = (h * 31 + text.charCodeAt(i)) | 0;
  return `${text.length}:${h}`;
}

function upsertVersioned(entry, base, version) {
  const list = entry.state.artifacts;
  const existing = list.find((a) => a.key === base.key);
  const fingerprint = contentFingerprint(version.html !== undefined ? version.html : version.spec);
  if (existing) {
    const versions = existing.versions || [];
    const last = versions[versions.length - 1];
    if (last && last.fingerprint === fingerprint) {
      upsertArtifact(entry, { key: base.key, ...versionFields(last), name: base.name || existing.name, order: ++entry.order });
      return;
    }
    const next = { ...version, n: versions.length + 1, fingerprint, saved: null };
    versions.push(next);
    upsertArtifact(entry, { key: base.key, ...base, versions, current: versions.length - 1, ...versionFields(next), order: ++entry.order });
    return;
  }
  const first = { ...version, n: 1, fingerprint, saved: null };
  upsertArtifact(entry, { ...base, versions: [first], current: 0, ...versionFields(first) });
}

function versionFields(v) {
  return { html: v.html, sources: v.sources, spec: v.spec, widgets: v.widgets, at: v.at };
}

function showVersion(entry, key, index) {
  const a = entry.state.artifacts.find((x) => x.key === key);
  if (!a || !a.versions || !a.versions[index]) return;
  upsertArtifact(entry, { key, current: index, ...versionFields(a.versions[index]) });
}

function markPageSaved(entry, { artifactId, version, name, slug, at }) {
  const a = entry.state.artifacts.find((x) => (x.kind === 'page' || x.kind === 'dashboard')
    && (artifactId ? x.id === artifactId : x.name === name));
  if (!a || !a.versions) return;
  const v = version ? a.versions.find((x) => x.n === version) : a.versions[a.current];
  if (!v) return;
  v.saved = { slug, at: at || new Date().toISOString() };
  upsertArtifact(entry, { key: a.key, status: 'saved', slug });
}

function pageArtifactId(doc, fallback) {
  const title = String(doc.title || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return title || fallback;
}

function htmlDocuments(text) {
  const out = [];
  let rest = String(text || '');
  let sources = [];
  let open = rest.indexOf('```html');
  while (open !== -1) {
    const before = rest.slice(0, open);
    const fence = before.match(/```kairon-sources\s*([\s\S]*?)```/);
    if (fence) {
      try {
        const parsed = JSON.parse(fence[1].trim());
        sources = Array.isArray(parsed) ? parsed : [];
      } catch { sources = []; }
    }
    const after = rest.slice(open + 7);
    const close = after.indexOf('```');
    if (close === -1) break;               // still streaming; wait for the close
    const html = after.slice(0, close).trim();
    if (html) {
      const heading = html.match(/<h1[^>]*>([^<]{1,80})<\/h1>/i);
      out.push({ html, sources, title: heading ? heading[1].trim() : '' });
    }
    sources = [];
    rest = after.slice(close + 3);
    open = rest.indexOf('```html');
  }
  return out;
}

function slugify(name) {
  const slug = String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64);
  return slug || '';
}

const ARTIFACT_KINDS = {
  metric: 'metric', update_metric: 'metric', rebuild_metric: 'metric',
  data_attribute: 'data_attribute', update_data_attribute: 'data_attribute',
  flag: 'flag', worklist: 'worklist', insight: 'insight',
  study: 'study', update_study: 'study', run_study: 'run', dataset: 'dataset',
  data_parser_schema: 'schema', update_data_parser_schema: 'schema',
  enable_schema: 'schema', parse_file: 'schema',
  routine: 'routine', update_routine: 'routine', run_parse: 'routine',
  task_type: 'task_type', update_task_type: 'task_type',
  task_action_type: 'task_action', update_task_action_type: 'task_action',
  metric_workflow: 'metric_workflow', task_status: 'task_status', update_task_status: 'task_status',
  analysis: 'analysis', update_analysis: 'analysis', run_analysis: 'analysis',
  page: 'page',
};

function sessionEntry(key) {
  if (!SESSIONS.has(key)) {
    SESSIONS.set(key, {
      key,
      state: reactive({
        connState: 'idle',
        closeReason: '',
        blocks: [],
        artifacts: [],
        totalCost: 0,
        thinkingSince: null,
        retryInSeconds: null,
        title: '',
        description: '',
        notebook: [],
        runCells: {},
        files: [],
        drafts: [],
        promptTokens: null,
        contextLimit: null,
        continuedBy: '',
        tools: null,
        workerAlive: false,
      }),
      order: 0,
      sessionId: null,
      cursor: 0,
      ownSeqs: new Set(),
      pollToken: 0,
      pollActive: false,
      posting: 0,
      lastEventAt: 0,
      artifactTimer: null,
      view: null,
      onWorkerOff: null,
      replayAt: null,
      pendingTurn: false,
      rowQueue: [],
      rowLoading: false,
    });
  }
  return SESSIONS.get(key);
}

function findEntryBySession(sessionId) {
  if (!sessionId) return null;
  for (const entry of SESSIONS.values()) {
    if (entry.sessionId === sessionId) return entry;
  }
  return null;
}

function entryForSession(sessionId, fallbackKey) {
  return findEntryBySession(sessionId) || sessionEntry(sessionId ? `session:${sessionId}` : fallbackKey);
}

const agentOverlay = reactive({ open: false });

function turnUrl(entry) {
  return `/api/impact_analytics/agent-sessions/${entry.sessionId}/turn/`;
}

function applyEnvelope(entry, data) {
  const s = entry.state;
  const map = {
    idle: 'ready', thinking: 'thinking', launching: 'launching',
    rate_limited: 'rate_limited', closed: 'closed',
  };
  let st = map[data.state] || 'ready';
  if (st === 'closed' && data.status !== 'died') st = 'ready';
  if (entry.pendingTurn && !data.worker_alive && st !== 'launching') entry.pendingTurn = false;
  if (entry.pendingTurn && st === 'ready') st = 'thinking';
  if (st === 'thinking' && s.connState !== 'thinking') s.thinkingSince = Date.now();
  if (st !== 'thinking') s.thinkingSince = null;
  s.connState = st;
  s.retryInSeconds = data.retry_in_seconds ?? null;
  s.workerAlive = !!data.worker_alive;
  if (typeof data.total_cost_usd === 'number') s.totalCost = data.total_cost_usd;
  if (data.title) s.title = data.title;
  if (typeof data.description === 'string') s.description = data.description;
  if (typeof data.prompt_tokens === 'number') s.promptTokens = data.prompt_tokens;
  if (typeof data.context_limit === 'number') s.contextLimit = data.context_limit;
  if (data.continued_by) s.continuedBy = data.continued_by;
  if (st === 'closed') s.closeReason = 'The agent process died.';
}

function hasPendingQuestion(entry) {
  return entry.state.blocks.some((b) => ['question', 'approval'].includes(b.kind) && !b.answered);
}

const sleep = (ms) => new Promise((r) => { setTimeout(r, ms); });

async function pollOnce(entry) {
  const { data } = await axios.get(
    `/api/impact_analytics/agent-sessions/${entry.sessionId}/stream/`,
    { params: { after: entry.cursor } },
  );
  let fresh = 0;
  for (const ev of data.events || []) {
    if (ev.seq > entry.cursor) entry.cursor = ev.seq;
    if (entry.ownSeqs.has(ev.seq)) continue;
    handleEvent(entry, ev);
    fresh += 1;
  }
  applyEnvelope(entry, data);
  return fresh;
}

function wakePolling(entry) {
  if (entry.pollActive || !entry.sessionId) return;
  const token = ++entry.pollToken;
  entry.pollActive = true;
  entry.lastEventAt = Date.now();
  (async () => {
    let errorDelay = 1000;
    let readyPolls = 0;
    try {
      while (entry.pollToken === token) {
        if (document.hidden) { await sleep(1000); continue; }
        if (entry.posting > 0) { await sleep(100); continue; }
        let fresh = 0;
        try {
          fresh = await pollOnce(entry);
          errorDelay = 1000;
        } catch (err) {
          const code = err && err.response && err.response.status;
          if (code === 404 || code === 401 || code === 403) {
            entry.state.connState = 'closed';
            entry.state.closeReason = 'Session unavailable.';
            return;
          }
          await sleep(errorDelay);
          errorDelay = Math.min(errorDelay * 2, 30000);
          continue;
        }
        if (fresh) entry.lastEventAt = Date.now();
        const st = entry.state.connState;
        if (st === 'closed') return;
        if (st === 'launching') {
          await sleep(1500);
          continue;
        }
        if (st === 'ready' || hasPendingQuestion(entry)) {
          if (!entry.state.workerAlive && entry.onWorkerOff) entry.onWorkerOff();
          readyPolls += 1;
          await sleep(readyPolls >= 5 && !fresh ? 20000 : 1000);
          continue;
        }
        readyPolls = 0;
        if (st === 'rate_limited') { await sleep(1500); continue; }
        const quiet = Date.now() - entry.lastEventAt;
        if (quiet < 6000) await sleep(300);
        else if (quiet < 30000) await sleep(1000);
        else await sleep(2500);
      }
    } finally {
      if (entry.pollToken === token) entry.pollActive = false;
    }
  })();
}

function stopPolling(entry) {
  entry.pollToken += 1;
  entry.pollActive = false;
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      SESSIONS.forEach((entry) => {
        const st = entry.state.connState;
        if (entry.sessionId && st !== 'closed' && st !== 'idle') wakePolling(entry);
      });
    }
  });
}

function artifactKey(kind, id) {
  return `${kind}:${id}`;
}

function upsertArtifact(entry, artifact) {
  const list = entry.state.artifacts;
  const i = list.findIndex((a) => a.key === artifact.key);
  if (i >= 0) {
    list.splice(i, 1, { ...list[i], ...artifact });
  } else {
    entry.order += 1;
    list.push({ order: entry.order, ...artifact });
    if (entry.view && NOTEBOOK_ARTIFACT_KINDS.has(artifact.kind)) entry.view.scrollArtifacts();
  }
}

function findCell(entry, id) {
  return entry.state.notebook.find((c) => c.id === id) || null;
}

function upsertCell(entry, { id, engine, code, label, version, by }) {
  if (!id) return null;
  let cell = findCell(entry, id);
  if (cell) {
    const changed = code !== undefined && code !== cell.code;
    if (changed) {
      cell.code = code;
      cell.version = version || cell.version + 1;
      cell.by = by || cell.by;
      if (cell.editing) {
        cell.editing = false;
        cell.draft = '';
      }
    } else if (version && version > cell.version) {
      cell.version = version;
    }
    if (label) cell.label = label;
    if (engine && ENGINES.has(engine)) cell.engine = engine;
    return cell;
  }
  entry.order += 1;
  cell = {
    kind: 'cell', key: `cell:${id}`, order: entry.order, id,
    engine: ENGINES.has(engine) ? engine : 'snowflake',
    code: code || '', label: label || '', version: version || 1, by: by || 'agent',
    result: null, editing: false, draft: '', saving: false, error: '',
  };
  entry.state.notebook.push(cell);
  if (entry.view) entry.view.scrollArtifacts();
  return cell;
}

function findDraft(entry, id) {
  return entry.state.drafts.find((d) => d.id === id) || null;
}

function upsertDraft(entry, ev) {
  if (!ev.id) return null;
  let draft = findDraft(entry, ev.id);
  const spec = ev.spec || {};
  if (draft) {
    const changed = JSON.stringify(spec) !== JSON.stringify(draft.spec);
    Object.assign(draft, {
      kind: ev.kind || draft.kind, name: ev.name || spec.name || draft.name, spec,
      version: ev.version || draft.version, by: ev.by || draft.by,
      status: ev.status || draft.status, savedId: ev.saved_id ?? draft.savedId,
      savedAt: ev.saved_at ?? draft.savedAt, error: ev.error || '',
      note: ev.note ?? draft.note, checkedAt: ev.checked_at ?? draft.checkedAt,
    });
    if (changed && draft.editing) {
      draft.editing = false;
      draft.edit = {};
    }
    return draft;
  }
  entry.order += 1;
  draft = {
    key: `draft:${ev.id}`, order: entry.order, id: ev.id, kind: ev.kind, label: DRAFT_LABELS[ev.kind] || ev.kind,
    name: ev.name || spec.name || ev.id, spec, version: ev.version || 1, by: ev.by || 'agent',
    status: ev.status || 'draft', savedId: ev.saved_id || null, savedAt: ev.saved_at || null, error: ev.error || '',
    note: ev.note || '', checkedAt: ev.checked_at || null, collapsed: true, previewedVersion: null,
    editing: false, edit: {}, saving: false, editError: '', flash: false,
  };
  entry.state.drafts.push(draft);
  if (entry.view) entry.view.scrollArtifacts();
  return draft;
}

function applyDraftEdit(entry, id, spec) {
  const draft = findDraft(entry, id);
  if (!draft || !spec || typeof spec !== 'object') return null;
  if (JSON.stringify(spec) === JSON.stringify(draft.spec)) return draft;
  const next = { ...spec };
  draft.spec = next;
  if (next.name) draft.name = next.name;
  draft.version += 1;
  draft.by = 'user';
  draft.error = '';
  if (draft.savedId) draft.status = 'changed';
  return draft;
}

function attachFile(entry, name, key) {
  if (!key) return;
  const files = entry.state.files.filter((f) => f.key !== key);
  files.push({ name: name || String(key).split('/').pop(), key });
  entry.state.files = files;
}

function cellForRun(entry, runId) {
  const id = entry.state.runCells[runId];
  return id ? findCell(entry, id) : null;
}

function removeArtifacts(entry, predicate) {
  entry.state.artifacts = entry.state.artifacts.filter((a) => !predicate(a));
}

function removeCells(entry, ids) {
  entry.state.notebook = entry.state.notebook.filter((c) => !ids.has(c.id));
}

function scheduleArtifactRefresh(entry) {
  clearTimeout(entry.artifactTimer);
  entry.artifactTimer = setTimeout(() => fetchArtifacts(entry), 400);
}

async function fetchArtifacts(entry) {
  if (!entry.sessionId) return;
  try {
    const { data } = await axios.get(`/api/impact_analytics/agent-sessions/${entry.sessionId}/artifacts/`);
    for (const a of data.artifacts || []) {
      upsertArtifact(entry, {
        key: artifactKey(a.kind, a.id),
        kind: a.kind,
        id: a.id,
        label: a.label,
        name: a.name,
        status: a.status,
        detail: a.detail || {},
        runs: a.runs || [],
        script: a.script || '',
        mono: a.kind === 'schema',
      });
    }
  } catch {
    // the ledger refreshes again on the next turn
  }
}

function handleEvent(entry, ev) {
  const s = entry.state;
  if (ev.type === 'ping') return;
  if (ev.type === 'status') {
    if (ev.state === 'launching' || ev.state === 'starting') s.connState = 'launching';
    else if (ev.state === 'rate_limited') {
      s.connState = 'rate_limited';
      s.retryInSeconds = ev.retry_in_seconds;
    } else if (ev.state === 'ready') {
      s.connState = 'ready';
      s.thinkingSince = null;
    } else if (ev.state === 'thinking') {
      s.connState = 'thinking';
      s.thinkingSince = Date.now();
    }
    return;
  }
  if (ev.type === 'approval_request') {
    pushBlock(entry, { kind: 'approval', operationId: ev.operation_id,
      operationKind: ev.kind, payload: ev.payload, answered: '' });
  } else if (ev.type === 'approval_result') {
    const block = entry.state.blocks.find((b) => b.kind === 'approval' && b.operationId === ev.operation_id);
    if (block) block.answered = ev.approved ? 'Approved' : 'Declined';
  } else if (ev.type === 'user') {
    if (ev.action || ev.run_block) {
      const action = ev.action || { verb: 'run', id: ev.run_block };
      pushBlock(entry, { kind: 'action', verb: action.verb, targetId: action.id });
    } else {
      if (!s.title) s.title = ev.text;
      pushBlock(entry, { kind: 'user', text: ev.text });
    }
  } else if (ev.type === 'text') {
    pushBlock(entry, { kind: 'text', text: ev.text });
  } else if (ev.type === 'text_delta') {
    let block = [...s.blocks].reverse().find((b) => b.kind === 'text' && b.id === ev.id && b.done === false);
    if (!block) {
      block = { kind: 'text', id: ev.id, text: '', done: false };
      pushBlock(entry, block);
    }
    block.text += ev.text;
    if (entry.view) entry.view.scrollChat();
  } else if (ev.type === 'text_end') {
    // The turn's text is complete: lift any ```html document out of it into the
    // workspace. A page belongs beside the notebook and the artifacts, not
    // inline in the conversation — the chat keeps the agent's prose lead and a
    // link. A preceding ```kairon-sources fence declares what the document may
    // read; without one it renders with no scripts at all.
    {
      const block = [...s.blocks].reverse().find((b) => b.kind === 'text' && b.id === ev.id && b.done === false);
      if (block) block.done = true;
      if (block) htmlDocuments(block.text).forEach((doc, i) => {
        const id = pageArtifactId(doc, `${ev.id}:${i}`);
        upsertVersioned(entry, { key: artifactKey('page', id), kind: 'page', id, label: 'Page', name: doc.title || 'Page', status: 'shown' },
          { html: doc.html, sources: doc.sources, at: new Date().toISOString() });
      });
    }
  } else if (ev.type === 'page_preview') {
    upsertVersioned(entry, { key: artifactKey('dashboard', ev.id), kind: 'dashboard', id: ev.id, label: 'Dashboard', name: ev.title || 'Dashboard', status: 'shown' },
      { spec: ev.spec || {}, widgets: ev.widgets || {}, at: ev.created_at || new Date().toISOString() });
  } else if (ev.type === 'page_saved') {
    if (ev.ok) markPageSaved(entry, { artifactId: ev.artifact, version: ev.version, name: ev.name, slug: ev.slug, at: ev.created_at });
    else pushBlock(entry, { kind: 'error', text: ev.error || 'The page could not be saved.' });
  } else if (ev.type === 'report') {
    pushBlock(entry, { kind: 'report', title: ev.title, items: ev.items || [] });
  } else if (ev.type === 'session_summary') {
    s.title = ev.title || s.title;
    s.description = ev.description || '';
  } else if (ev.type === 'ask_user') {
    pushBlock(entry, { kind: 'question', callId: ev.call_id, question: ev.question,
      options: ev.options || [], responseText: '', answered: '' });
  } else if (ev.type === 'user_answer') {
    const block = [...s.blocks].reverse().find((b) => b.kind === 'question' && b.callId === ev.call_id);
    if (block) block.answered = ev.label || ev.text || 'Written response';
  } else if (ev.type === 'thinking_start') {
    thinkingBlock(entry, ev.id);
  } else if (ev.type === 'thinking_delta') {
    const block = thinkingBlock(entry, ev.id);
    block.text += ev.text;
  } else if (ev.type === 'thinking_end') {
    const block = [...s.blocks].reverse().find((b) => b.kind === 'thinking');
    if (block) block.done = true;
  } else if (ev.type === 'drawing') {
    upsertArtifact(entry, {
      key: artifactKey('drawing', ev.id), kind: 'drawing', id: ev.id,
      label: 'Drawing', name: ev.title || 'Untitled', status: 'shown', svg: ev.svg,
    });
  } else if (ev.type === 'visual') {
    if (ev.rows_deferred) {
      upsertArtifact(entry, {
        key: artifactKey('visual', ev.id), kind: 'visual', id: ev.id,
        label: 'Visual', name: ev.title || 'Untitled', subtitle: ev.viz_type,
        status: 'shown', viz: null, pending: true, rowsSeq: ev.seq, rowCount: ev.row_count,
      });
      entry.rowQueue = entry.rowQueue.filter((q) => q.id !== ev.id);
      entry.rowQueue.push({ id: ev.id, seq: ev.seq });
      loadVisualRows(entry);
    } else {
      upsertArtifact(entry, {
        key: artifactKey('visual', ev.id), kind: 'visual', id: ev.id,
        label: 'Visual', name: ev.title || 'Untitled', subtitle: ev.viz_type,
        status: 'shown', viz: vizFor(ev), pending: false,
      });
    }
  } else if (ev.type === 'scratchpad_clear' || ev.type === 'artifacts_removed') {
    const ids = ev.ids && ev.ids.length ? new Set(ev.ids) : null;
    removeArtifacts(entry, (a) => (a.kind === 'visual' || a.kind === 'drawing') && (!ids || ids.has(a.id)));
    if (ids) removeCells(entry, ids);
  } else if (ev.type === 'block') {
    upsertCell(entry, { id: ev.id, engine: ev.engine, code: ev.code, label: ev.label, version: ev.version, by: ev.by });
  } else if (ev.type === 'draft') {
    upsertDraft(entry, ev);
  } else if (ev.type === 'draft_removed') {
    s.drafts = s.drafts.filter((d) => d.id !== ev.id);
  } else if (ev.type === 'draft_edit') {
    applyDraftEdit(entry, ev.draft_id, ev.spec);
  } else if (ev.type === 'file_attached') {
    attachFile(entry, ev.name, ev.key);
    pushBlock(entry, { kind: 'upload', ok: true, name: ev.name, key: ev.key });
  } else if (ev.type === 'block_edit') {
    const cell = upsertCell(entry, { id: ev.block_id, engine: ev.engine, code: ev.code, by: 'user' });
    if (cell && cell.by !== 'user') cell.by = 'user';
  } else if (ev.type === 'block_result') {
    const cell = upsertCell(entry, { id: ev.id, engine: ev.engine });
    if (cell && String(ev.id).startsWith('preview_')) {
      const draft = findDraft(entry, String(ev.id).slice('preview_'.length));
      if (draft) draft.previewedVersion = draft.version;
    }
    if (cell) {
      cell.result = {
        kind: 'sql', run: ev.run, version: ev.version, ok: !!ev.ok,
        columns: ev.columns || Object.keys((ev.rows || [])[0] || {}), rows: ev.rows || [],
        rowCount: ev.row_count, truncated: !!ev.truncated, elapsedMs: ev.elapsed_ms, error: ev.error || '',
        ranAt: ev.ran_at || null,
      };
    }
  } else if (ev.type === 'block_run') {
    const cell = upsertCell(entry, { id: ev.id, engine: 'r' });
    if (cell) {
      s.runCells[ev.run_id] = cell.id;
      const previous = s.artifacts.find((a) => a.kind === 'run' && a.id === ev.run_id);
      cell.result = {
        kind: 'r', version: cell.version, runId: ev.run_id, status: ev.status || 'pending', ranAt: ev.ran_at || null,
        console: (previous && previous.console) || '', error: (previous && previous.error) || '',
        outputs: (previous && previous.outputs) || [], plotUrls: (previous && previous.plotUrls) || {},
      };
      removeArtifacts(entry, (a) => a.kind === 'run' && a.id === ev.run_id);
      if (entry.view) {
        if (['pending', 'running'].includes(cell.result.status)) entry.view.startRunPolling(ev.run_id);
        else entry.view.fetchRunPlots(ev.run_id);
      }
    }
  } else if (ev.type === 'sources_update') {
    upsertArtifact(entry, {
      key: artifactKey('plan', 'sources'), kind: 'plan', id: 'sources',
      label: 'Plan', name: 'Data sources', status: 'planned', sources: ev.sources || [],
    });
  } else if (ev.type === 'r_code') {
    const name = String(ev.name || 'analysis.R').replace(/\.R$/i, '');
    upsertCell(entry, { id: ev.id || slugify(name) || 'analysis', engine: 'r', code: ev.script || '', label: name });
  } else if (ev.type === 'r_output') {
    if (ev.block_id && !s.runCells[ev.run_id]) {
      const cell = upsertCell(entry, { id: ev.block_id, engine: 'r' });
      if (cell) {
        s.runCells[ev.run_id] = cell.id;
        if (!cell.result || cell.result.runId !== ev.run_id) {
          cell.result = { kind: 'r', version: cell.version, runId: ev.run_id, status: '', console: '', error: '', outputs: [], plotUrls: {} };
        }
      }
    }
    applyRunOutput(entry, ev);
  } else if (ev.type === 'file_sample') {
    upsertArtifact(entry, {
      key: artifactKey('file', ev.key), kind: 'file', id: ev.key,
      label: 'File', name: (ev.key || '').split('/').pop(), mono: true,
      status: 'read', key: ev.key, note: ev.note, offset: ev.offset || 0, rows: ev.rows || [],
    });
  } else if (ev.type === 'file_ready') {
    upsertArtifact(entry, {
      key: artifactKey('report', ev.id || ev.key), kind: 'report', id: ev.id || ev.key,
      label: 'Report', name: ev.name, title: ev.title || ev.name, mono: false,
      status: 'ready', s3Key: ev.key, size: ev.size || 0,
    });
    pushBlock(entry, { kind: 'download', name: ev.name, title: ev.title || ev.name, s3Key: ev.key, size: ev.size || 0 });
  } else if (ev.type === 'file_text') {
    upsertArtifact(entry, {
      key: artifactKey('document', `${ev.key}@${ev.offset || 0}`), kind: 'document', id: ev.key,
      label: 'Document', name: (ev.key || '').split('/').pop(), mono: true,
      status: 'read', key: ev.key, note: ev.note, offset: ev.offset || 0, end: ev.end || 0, total: ev.total || 0, text: ev.text || '',
    });
  } else if (ev.type === 'schema_proposal') {
    const name = `${ev.snowflake_schema}.${ev.table_name}`;
    upsertArtifact(entry, {
      key: artifactKey('proposal', name), kind: 'proposal', id: name,
      label: 'Schema proposal', name, mono: true,
      status: ev.status === 'created' ? 'draft created' : ev.status,
      columns: ev.columns || [], primary_key_columns: ev.primary_key_columns || [],
      append_only: ev.append_only, append_only_reason: ev.append_only_reason, error: ev.error,
    });
  } else if (ev.type === 'create_result') {
    const kind = ARTIFACT_KINDS[ev.artifact_kind] || 'artifact';
    const label = ARTIFACT_LABELS[ev.artifact_kind] || 'Artifact';
    const key = artifactKey(kind, ev.id);
    if (ev.ok && ev.id && kind === 'run') {
      upsertArtifact(entry, {
        key, kind, id: ev.id, label, name: ev.name || `Run ${ev.id}`,
        status: 'pending', console: '', error: '', outputs: [], plotUrls: {},
      });
      if (entry.view) entry.view.startRunPolling(ev.id);
    } else if (ev.ok && ev.id) {
      upsertArtifact(entry, {
        key, kind, id: ev.id, label, name: ev.name || `${label} ${ev.id}`,
        status: 'saved', detail: {}, runs: [], mono: kind === 'schema',
      });
      if (ev.artifact_kind === 'page') markPageSaved(entry, { name: ev.name, slug: ev.slug || '', at: ev.created_at });
      scheduleArtifactRefresh(entry);
    }
    pushBlock(entry, {
      kind: 'created', ok: ev.ok, artifactLabel: label, action: ev.action || 'created',
      id: ev.id, name: ev.name, error: ev.error, artifactKey: key,
    });
  } else if (ev.type === 'tools') {
    s.tools = { available: ev.available || [], unavailable: ev.unavailable || [] };
  } else if (ev.type === 'continued_from') {
    pushBlock(entry, { kind: 'meta', text: `Continued from “${ev.title || ev.session_id}”. Its artifacts are carried over.` });
  } else if (ev.type === 'turn_done') {
    entry.pendingTurn = false;
    if (ev.cost_usd) s.totalCost += ev.cost_usd;
    if (typeof ev.prompt_tokens === 'number') s.promptTokens = ev.prompt_tokens;
    if (typeof ev.context_limit === 'number') s.contextLimit = ev.context_limit;
    scheduleArtifactRefresh(entry);
  } else if (ev.type === 'stopped') {
    s.blocks.forEach((block) => {
      if (['question', 'approval'].includes(block.kind) && !block.answered) block.answered = '(expired)';
    });
    pushBlock(entry, { kind: 'stopped' });
  } else if (ev.type === 'session_end') {
    scheduleArtifactRefresh(entry);
  } else if (ev.type === 'error' || ev.type === 'fatal') {
    if (ev.type === 'fatal') entry.pendingTurn = false;
    pushBlock(entry, { kind: 'error', text: ev.message });
  }
}

function vizFor(ev) {
  try {
    const viz = buildVizProps(ev.viz_type, ev.viz_config, ev.rows);
    if (viz && viz.props && ev.inputs && typeof ev.inputs === 'object') viz.props.inputs = ev.inputs;
    return viz;
  } catch {
    return null;
  }
}

async function loadVisualRows(entry) {
  if (entry.rowLoading || !entry.sessionId) return;
  entry.rowLoading = true;
  try {
    while (entry.rowQueue.length) {
      const next = entry.rowQueue.pop();
      const artifact = entry.state.artifacts.find((a) => a.kind === 'visual' && a.id === next.id);
      if (!artifact || !artifact.pending) continue;
      try {
        const { data } = await axios.get(`/api/impact_analytics/agent-sessions/${entry.sessionId}/events/${next.seq}/`);
        upsertArtifact(entry, { key: artifactKey('visual', next.id), viz: vizFor(data), pending: false });
      } catch {
        upsertArtifact(entry, { key: artifactKey('visual', next.id), viz: null, pending: false });
      }
    }
  } finally {
    entry.rowLoading = false;
  }
}

function pushBlock(entry, block) {
  if (entry.replayAt && block.at === undefined) block.at = entry.replayAt;
  entry.state.blocks.push(block);
  if (entry.view) entry.view.scrollChat();
}

const CONVERSATION_EVENT_TYPES = new Set(['user', 'text', 'text_delta', 'text_end', 'thinking_start', 'thinking_delta', 'thinking_end']);

function messageBlocks(messages) {
  const out = [];
  for (const m of messages || []) {
    const parts = (m.content && m.content.parts) || [];
    if (parts.some((p) => p.function_response)) continue;
    const text = parts.filter((p) => p.text && !p.thought).map((p) => p.text).join('');
    if (!text) continue;
    out.push(m.role === 'user'
      ? { kind: 'user', text, at: m.created_at }
      : { kind: 'text', id: `stored-${m.sort_order}`, text, done: true, at: m.created_at });
  }
  return out;
}

function replayHistory(entry, events, messages) {
  for (const ev of events || []) {
    if (ev.seq > entry.cursor) entry.cursor = ev.seq;
    if (CONVERSATION_EVENT_TYPES.has(ev.type)) continue;
    entry.replayAt = ev.created_at || '';
    handleEvent(entry, ev);
  }
  entry.replayAt = null;
  const stored = messageBlocks(messages);
  stored.forEach((block) => {
    if (block.kind !== 'text') return;
    htmlDocuments(block.text).forEach((doc, i) => {
      const id = pageArtifactId(doc, `${block.id}:${i}`);
      upsertVersioned(entry, { key: artifactKey('page', id), kind: 'page', id, label: 'Page', name: doc.title || 'Page', status: 'shown' },
        { html: doc.html, sources: doc.sources, at: block.at });
    });
  });
  const blocks = entry.state.blocks.concat(stored);
  blocks.sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')));
  entry.state.blocks = blocks;
}

function thinkingBlock(entry, id) {
  const last = entry.state.blocks[entry.state.blocks.length - 1];
  if (last && last.kind === 'thinking') {
    last.id = id;
    last.done = false;
    return last;
  }
  const block = { kind: 'thinking', id, text: '', done: false, collapsed: true };
  pushBlock(entry, block);
  return block;
}

function runHolder(entry, runId) {
  const cell = cellForRun(entry, runId);
  if (cell && cell.result && cell.result.kind === 'r' && cell.result.runId === runId) return cell.result;
  return entry.state.artifacts.find((a) => a.key === artifactKey('run', runId)) || null;
}

function applyRunOutput(entry, data) {
  const cell = cellForRun(entry, data.run_id);
  const outputs = (data.results && data.results.outputs) || [];
  if (cell && cell.result && cell.result.kind === 'r' && cell.result.runId === data.run_id) {
    cell.result.status = data.status;
    cell.result.error = data.error || '';
    cell.result.console = data.console || '';
    cell.result.outputs = outputs;
  } else {
    const key = artifactKey('run', data.run_id);
    const existing = entry.state.artifacts.find((a) => a.key === key) || {};
    upsertArtifact(entry, {
      key, kind: 'run', id: data.run_id, label: 'R run',
      name: existing.name || `Run ${data.run_id}`,
      status: data.status,
      error: data.error || '',
      console: data.console || '',
      outputs,
      plotUrls: existing.plotUrls || {},
    });
  }
  if (!entry.view) return;
  if (['pending', 'running'].includes(data.status)) {
    entry.view.startRunPolling(data.run_id);
  } else {
    entry.view.stopRunPolling(data.run_id);
    entry.view.fetchRunPlots(data.run_id);
  }
}


export { showVersion, sessionEntry, findEntryBySession, entryForSession, agentOverlay, hasPendingQuestion, wakePolling, stopPolling, handleEvent, replayHistory, fetchArtifacts, pushBlock, turnUrl, applyEnvelope, applyRunOutput, artifactKey, upsertArtifact, upsertCell, findCell, runHolder, attachFile, applyDraftEdit, DRAFT_LEDGER_KIND, NOTEBOOK_ARTIFACT_KINDS };
